import { getItemFromStorage } from "./script"
import { practicesOptions } from "./systemData"
import { docsFunctions } from "./docs"

const PROCODOC_FLAG_CONFIG = {
    HIPAA: {
        practiceFlag: 'has_hippa',
        attorneyList: 'has_hippa_list',
        requiresPractice: (practiceId) => practicesFunctions.isPracticeRequiresHIPAA(practiceId),
    },
    LOP: {
        practiceFlag: 'has_lop',
        attorneyList: 'has_lop_list',
        requiresPractice: (practiceId) => practicesFunctions.isPracticeRequiresLOP(practiceId),
    },
}

export const practicesFunctions = {
    listFlagForPractice: (list, practiceId) => {
        if (!list || practiceId == null || practiceId === '') {
            return 0
        }

        const value = list[practiceId] ?? list[String(practiceId)]

        return value > 0 ? 1 : 0
    },
    resolveCaseAttorney: (caseAttorney, activeCase, caseAttorneyId) => {
        if (caseAttorney) {
            return caseAttorney
        }
        if (!activeCase) {
            return null
        }
        const fromList = activeCase.attorneys?.find(item => item.id == caseAttorneyId)
        if (fromList) {
            return fromList
        }
        // Require case_attorney to exist before id compare — `undefined == null` is true.
        if (activeCase.case_attorney && activeCase.case_attorney.id == caseAttorneyId) {
            return activeCase.case_attorney
        }
        return null
    },
    isProcodocSatisfiedForPractice: (useFor, practiceId, practiceRow, caseAttorney, activeCase, caseAttorneyId) => {
        const config = PROCODOC_FLAG_CONFIG[useFor]
        if (!config) {
            return false
        }

        const attorney = practicesFunctions.resolveCaseAttorney(caseAttorney, activeCase, caseAttorneyId)

        if (practicesFunctions.listFlagForPractice(attorney?.[config.attorneyList], practiceId) === 1) {
            return true
        }

        if (practiceId === 'all' && attorney?.[config.attorneyList]) {
            return Object.values(attorney[config.attorneyList]).some(value => Number(value) > 0)
        }

        // Work queues send practices.lop; case practices send has_lop.
        const practiceFlag = practiceRow?.[config.practiceFlag] ?? (useFor === 'LOP' ? practiceRow?.lop : undefined)
        if (practiceFlag == 1 || practiceFlag === true) {
            return true
        }

        return false
    },
    isHippaSatisfiedForPractice: (practiceId, practiceRow, caseAttorney, activeCase, caseAttorneyId) => {
        return practicesFunctions.isProcodocSatisfiedForPractice('HIPAA', practiceId, practiceRow, caseAttorney, activeCase, caseAttorneyId)
    },
    isLopSatisfiedForPractice: (practiceId, practiceRow, caseAttorney, activeCase, caseAttorneyId) => {
        return practicesFunctions.isProcodocSatisfiedForPractice('LOP', practiceId, practiceRow, caseAttorney, activeCase, caseAttorneyId)
    },
    isDocumentUploadedForPractice: (procodocsDocuments, practiceId, caseAttorneyId, documentTypeId, practiceRow, caseAttorney, useFor, activeCase = null) => {
        if (useFor && practicesFunctions.isProcodocSatisfiedForPractice(useFor, practiceId, practiceRow, caseAttorney, activeCase, caseAttorneyId)) {
            return true
        }

        if (procodocsDocuments && practiceId && caseAttorneyId && documentTypeId) {
            return Boolean(docsFunctions.getUploadedDocumentState(procodocsDocuments, documentTypeId, practiceId, caseAttorneyId))
        }
    
        return false 
    },
    isAnyPracticeMissingDocument: (procodocsDocuments, practicesList, caseAttorneyId, documentTypeId, caseAttorney, useFor = 'HIPAA', activeCase = null) => {
        return practicesFunctions.isAnyPracticeMissingDocumentByUseFor(
            procodocsDocuments,
            practicesList,
            caseAttorneyId,
            documentTypeId,
            caseAttorney,
            useFor,
            activeCase
        )
    },
    isAnyPracticeMissingDocumentByUseFor: (procodocsDocuments, practicesList, caseAttorneyId, documentTypeId, caseAttorney, useFor, activeCase = null) => {
        const config = PROCODOC_FLAG_CONFIG[useFor]
        if (!config) {
            return false
        }

        const practicesArr = Array.isArray(practicesList) ? practicesList : []

        if (practicesArr.length === 0 || !caseAttorneyId || !documentTypeId) {
            return false
        }

        let filteredPractices = practicesArr.filter(item => config.requiresPractice(item?.practice_id))

        for (const practice of filteredPractices) {
            let found = practicesFunctions.isDocumentUploadedForPractice(
                procodocsDocuments,
                practice?.practice_id,
                caseAttorneyId,
                documentTypeId,
                practice,
                caseAttorney,
                useFor,
                activeCase
            )

            if (!found) {
                return true
            }
        }
        return false
    },
    isPracticeRequiresHIPAA: (practice_id) => {
        const systemPractices = practicesOptions()
        const practice = systemPractices.find(item => item.value == practice_id)
        if (practice) {
            return practice.requires_hippa ? true : false
        }
        return false
    },
    isPracticeRequiresLOP: (practice_id) => {
        const systemPractices = practicesOptions()
        const practice = systemPractices.find(item => item.value == practice_id)
        if (practice) {
            return practice.requires_lop ? true : false
        }
        return false
    },
    isPracticeLienEligible: (practice_id) => {
        const systemPractices = practicesOptions()
        const practice = systemPractices.find(item => item.value == practice_id)
        if (practice) {
            return practice.lien_eligible ? true : false
        }
        return false
    },
    getPracticeType: (practice_id) => {
        const systemSettings = getItemFromStorage('settings')
        if (systemSettings && systemSettings.practices) {
            let item = systemSettings.practices.find(item => item.id == practice_id)
            if (item) {
                return item.practice_type
            }
        }
        return null
    },
    getAICAChartNumber: (activeCase) => {
        if (activeCase && activeCase.practices) {
            let aica = activeCase.practices.find(item => item.practice_id == 1) // AICA practice id is 1
            if (aica) {
                return aica.chart_number
            }
        }
        return null
    },
}
